/**
 * Lightweight, client-side detection of the whitelisted video providers the
 * backend accepts for activity gallery embeds (YouTube / Facebook). This mirrors
 * the server whitelist in App\Support\VideoEmbed for immediate UX feedback ONLY —
 * the server remains authoritative and returns 422 on anything it rejects.
 *
 * Returns 'youtube' | 'facebook' | 'facebook_post' | null.
 */
export function detectVideoProvider(rawUrl) {
  const url = (rawUrl || '').trim();
  if (!url) return null;

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (!/^https?:$/.test(parsed.protocol)) return null;

  const host = parsed.hostname.toLowerCase().replace(/^(www\.|m\.|web\.)/, '');
  const path = parsed.pathname;

  // YouTube: youtu.be/<id>, youtube.com/watch?v=, /shorts/<id>, /embed/<id>, /v/<id>
  if (host === 'youtu.be') {
    if (/^\/[A-Za-z0-9_-]{11}/.test(path)) return 'youtube';
    return null;
  }
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (path === '/watch' && /^[A-Za-z0-9_-]{11}$/.test(parsed.searchParams.get('v') || '')) {
      return 'youtube';
    }
    if (/^\/(?:shorts|embed|v)\/[A-Za-z0-9_-]{11}/.test(path)) return 'youtube';
    return null;
  }

  // Facebook: fb.watch/<code>, facebook.com/watch?v=<digits>,
  // facebook.com/<page>/videos/<id>, facebook.com/reel/<id>
  if (host === 'fb.watch') {
    if (/^\/[A-Za-z0-9_-]{3,64}/.test(path)) return 'facebook';
    return null;
  }
  if (host === 'facebook.com') {
    if (/^\/watch\/?$/.test(path) && /^\d+$/.test(parsed.searchParams.get('v') || '')) {
      return 'facebook';
    }
    if (/^\/[A-Za-z0-9.-]+\/videos\/\d+/.test(path)) return 'facebook';
    if (/^\/reel\/\d+/.test(path)) return 'facebook';

    // Public posts (spec 2026-09-16 §3.1) — mirrors App\Support\VideoEmbed::facebookPost().
    const alnum = /^[A-Za-z0-9]+$/;
    if (/^\/[A-Za-z0-9.-]+\/posts\/[A-Za-z0-9]+\/?$/.test(path)) return 'facebook_post';
    if (
      path === '/permalink.php' &&
      alnum.test(parsed.searchParams.get('story_fbid') || '') &&
      alnum.test(parsed.searchParams.get('id') || '')
    ) {
      return 'facebook_post';
    }
    if (/^\/share\/p\/[A-Za-z0-9_-]{3,64}\/?$/.test(path)) return 'facebook_post';
    if (
      (path === '/photo' || path === '/photo/' || path === '/photo.php') &&
      /^\d+$/.test(parsed.searchParams.get('fbid') || '')
    ) {
      return 'facebook_post';
    }
    if (/^\/[A-Za-z0-9.-]+\/photos\/[A-Za-z0-9.-]+\/[A-Za-z0-9.-]+\/?$/.test(path)) return 'facebook_post';
    return null;
  }

  return null;
}

/** Human label for a provider key. */
export function providerLabel(provider) {
  if (provider === 'youtube') return 'YouTube';
  if (provider === 'facebook') return 'Facebook';
  if (provider === 'facebook_post') return 'Facebook post';
  return 'Video link';
}
